import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Empty } from 'antd';
import clsx from 'clsx';
import { ConversationStatus, PubsubChannelType } from 'sb-lib/types';
import { useAppDispatch, useAppSelector } from 'shared/store';
import { useSocketSubscription } from 'shared/hooks';
import {
  fetchVisitorConversations,
  selectVisitorConversations,
} from 'shared/store/conversation-slice';
import EmptyStateBellHappy from 'shared/assets/images/empty-state-bell-happy.svg';
import Loader from 'shared/components/Loader';
import { ToggleCard } from 'shared/components/ToggleCard';
import { Button } from 'shared/components/Button';
import GlobalConversationHistoryItem from './GlobalConversationHistoryItem';
import './style.less';

interface GlobalConversationHistoryListProps {
  visitorId: string;
  conversationStatus: ConversationStatus;
  setConversationStatus: React.Dispatch<React.SetStateAction<ConversationStatus>>;
  openConversation: (visitorId: string, conversationId: number) => void;
  size: string;
}

export const GlobalConversationHistoryList: React.FC<GlobalConversationHistoryListProps> = ({
  visitorId,
  conversationStatus,
  setConversationStatus,
  openConversation,
  size,
}) => {
  const dispatch = useAppDispatch();
  const {
    isFetchingVisitorConversations,
    visitorConversationsError,
    hasMoreVisitorConversations,
    isFetchingMoreVisitorConversations,
  } = useAppSelector(s => s.conversation);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const allConversations = useAppSelector(selectVisitorConversations);

  const recentlyToggledConversations = useMemo(() => {
    const shouldBeOpen = conversationStatus !== ConversationStatus.OPEN;
    return allConversations.filter(c => c.isOpen === shouldBeOpen).map(c => c.id);
  }, [conversationStatus, allConversations]);

  const allConversationsTotal = useAppSelector(
    s => s.conversation.visitorConversationsTotal,
  );

  const conversationsTotal = allConversationsTotal
    ? allConversationsTotal - recentlyToggledConversations.length
    : 0;
  const conversations = allConversations.filter(
    c => !recentlyToggledConversations.includes(c.id),
  );

  const loadConversations = useCallback(
    async (dateLastMessage?: number, signal?: AbortSignal) => {
      if (!visitorId) return;
      const thunkPromise = dispatch(
        fetchVisitorConversations({
          visitorId,
          dateLastMessage,
          status: conversationStatus,
        }),
      );
      thunkPromise.then(() => !signal?.aborted && setIsLoaded(true));
      if (signal) {
        signal.addEventListener('abort', () => {
          (thunkPromise as any).abort();
        });
      }
    },
    [dispatch, conversationStatus, visitorId],
  );

  useEffect(() => {
    const ac = new AbortController();
    loadConversations(undefined, ac.signal);
    return () => {
      ac.abort();
    };
  }, [loadConversations]);

  useSocketSubscription(PubsubChannelType['chat.conversations.visitor'], visitorId);

  const dateLastMessage = conversations[conversations.length - 1]?.dateLastMessage;

  return (
    <div
      className={clsx(
        'GlobalConversationHistoryListWrapper',
        isOpen && 'GlobalConversationHistoryListWrapper--open',
      )}
    >
      {!isOpen && conversationsTotal != null && (
        <div className="GlobalConversationHistoryListWrapper__pill">
          {conversationsTotal} {conversationStatus}
        </div>
      )}
      <ToggleCard
        ulsKey="GlobalConversationHistoryList"
        header="Conversations"
        callback={setIsOpen}
        isDefaultOpen={false}
      >
        <div className="GlobalConversationHistoryList">
          <div className={clsx('GlobalConversationHistoryList__Toggle')}>
            <div
              className={clsx(
                'GlobalConversationHistoryList__Toggle-gradient',
                conversationStatus === ConversationStatus.CLOSED &&
                  'GlobalConversationHistoryList__Toggle-gradient--right',
              )}
            />
            <button
              type="button"
              className={clsx(
                'GlobalConversationHistoryList__Toggle-button',
                conversationStatus === ConversationStatus.OPEN &&
                  'GlobalConversationHistoryList__Toggle-button--active',
              )}
              onClick={() => {
                setIsLoaded(false);
                setConversationStatus(ConversationStatus.OPEN);
              }}
            >
              Open
            </button>
            <button
              type="button"
              className={clsx(
                'GlobalConversationHistoryList__Toggle-button',
                conversationStatus === ConversationStatus.CLOSED &&
                  'GlobalConversationHistoryList__Toggle-button--active',
              )}
              onClick={() => {
                setIsLoaded(false);
                setConversationStatus(ConversationStatus.CLOSED);
              }}
            >
              Closed
            </button>
          </div>
          {!!visitorConversationsError && visitorConversationsError !== 'Aborted' && (
            <div className="GlobalConversationHistoryList__error">
              {visitorConversationsError}
            </div>
          )}
          {((!isLoaded && isFetchingVisitorConversations) ||
            visitorConversationsError === 'Aborted') && (
            <Loader
              inline
              size="small"
              className="GlobalConversationHistoryList__loader"
            />
          )}
          {isLoaded && conversations.length === 0 && (
            <Empty
              className="GlobalConversationHistoryList__empty"
              image={<EmptyStateBellHappy height={54} />}
              description="No recent conversations"
            />
          )}
          {conversations.length > 0 && (
            <div
              className={clsx(
                'GlobalConversationHistoryList__conversations',
                `GlobalConversationHistoryList__conversations--${size}`,
              )}
            >
              {conversations.map(c => (
                <GlobalConversationHistoryItem
                  key={c.id}
                  conversation={c}
                  openConversation={openConversation}
                />
              ))}
            </div>
          )}
          {hasMoreVisitorConversations && dateLastMessage && (
            <Button
              className="GlobalConversationHistoryList__loadMore"
              loading={isFetchingMoreVisitorConversations}
              onClick={() => loadConversations(dateLastMessage)}
            >
              Load more
            </Button>
          )}
        </div>
      </ToggleCard>
    </div>
  );
};
